import { z } from 'zod';
import { MoneySchema } from './common.js';
// Prisma Decimal columns serialize as canonical strings; allow either string
// or non-negative number on the wire (matches the convention used in
// journal-entry.ts / loan.ts / project.ts).
const DecimalLike = z.union([MoneySchema, z.coerce.number().nonnegative()]);
// Signed money for running / opening / closing balances. DecimalLike (and the
// underlying MoneySchema) are NON-NEGATIVE only; a statement's opening/running
// balance can legitimately go negative after an over-receipt, so balances use a
// permissive string|number union (no non-negative refinement).
const SignedDecimalLike = z.union([z.string(), z.number()]);
// ============================================================
// WHT (withholding tax) presets + cert status enum
// ============================================================
// Standard Thai WHT income-type presets (editable in the UI). Rates are the
// common s.50bis withholding rates; the physical 50-ทวิ is authoritative.
export const WHT_INCOME_TYPES = {
    service: { label: 'ค่าบริการ/รับจ้างทำของ', defaultRate: 3 },
    transport: { label: 'ค่าขนส่ง', defaultRate: 1 },
    rental: { label: 'ค่าเช่า', defaultRate: 5 },
    advertising: { label: 'ค่าโฆษณา', defaultRate: 2 },
    professional: { label: 'ค่าวิชาชีพอิสระ', defaultRate: 3 },
};
export const ArWhtCertStatusSchema = z.enum(['PENDING', 'RECEIVED', 'VOID']);
// ============================================================
// ArInvoiceStatus enum
// ============================================================
export const ArInvoiceStatusSchema = z.enum(['POSTED', 'PAID', 'VOID']);
// ============================================================
// ArInvoiceLine — child rows of an ArInvoice
// ============================================================
export const ArInvoiceLineSchema = z.object({
    id: z.uuid(),
    invoiceId: z.uuid(),
    lineNo: z.number().int().positive(),
    description: z.string().min(1),
    qty: DecimalLike,
    unitPrice: DecimalLike,
    amount: DecimalLike,
    revenueCode: z.string().min(1),
});
// Wire shape — id/invoiceId/lineNo/amount are server-derived.
export const CreateArInvoiceLineSchema = z.object({
    description: z.string().min(1),
    qty: z.coerce.number().positive(),
    unitPrice: z.coerce.number().nonnegative(),
    // Optional: when blank the service resolves SALES_DOMESTIC (the company's
    // configured default revenue account, set in /settings/account-mapping).
    revenueCode: z.string().trim().optional(),
});
// ============================================================
// ArInvoice — header
// ============================================================
// FX read-shape: nullable for THB rows, populated for non-THB. Use z.union
// with MoneySchema so Prisma's Decimal serialization (string) passes through;
// z.coerce.number() handles the seed/test fast-path.
const ForeignAmountLike = z.union([MoneySchema, z.coerce.number()]).nullable().optional();
export const ArInvoiceSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    voucherNo: z.string().min(1),
    invoiceDate: z.coerce.date(),
    dueDate: z.coerce.date(),
    customerId: z.uuid(),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    description: z.string().nullable().optional(),
    subtotal: DecimalLike,
    vatRate: DecimalLike,
    vatAmount: DecimalLike,
    totalAmount: DecimalLike,
    paidAmount: DecimalLike,
    // Cumulative credit-note amount applied against this invoice (Phase 2b).
    // Defaults to 0 on the wire; outstanding = totalAmount - paidAmount - creditedAmount.
    creditedAmount: DecimalLike,
    // FX — defaults to THB; foreignAmount + exchangeRateAtBooking are null for
    // THB rows, populated for foreign currency rows.
    currencyCode: z.string().default('THB'),
    foreignAmount: ForeignAmountLike,
    exchangeRateAtBooking: ForeignAmountLike,
    status: ArInvoiceStatusSchema,
    journalEntryId: z.uuid().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
// List-row shape — adds the derived agingDays + display names for table
// rendering. agingDays is computed server-side relative to today.
export const ArInvoiceListRowSchema = ArInvoiceSchema.extend({
    agingDays: z.number().int(),
    customerName: z.string(),
    branchName: z.string().nullable().optional(),
});
// Detail shape — includes lines + allocations + display names.
export const ArInvoiceWithLinesSchema = ArInvoiceSchema.extend({
    lines: z.array(ArInvoiceLineSchema),
    customerName: z.string(),
    branchName: z.string().nullable().optional(),
    agingDays: z.number().int(),
});
// Wire shape for POST — voucherNo / subtotal / vatAmount / totalAmount are all
// generated server-side. Dates are wire-as-ISO per Phase 3b convention.
export const CreateArInvoiceSchema = z.object({
    invoiceDate: z.iso.date(),
    dueDate: z.iso.date(),
    customerId: z.uuid(),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    description: z.string().optional(),
    vatRate: z.coerce.number().min(0).max(100).default(7),
    // FX — currencyCode defaults to THB at the service layer (we keep it
    // optional on the wire so existing clients don't need to change). When
    // currencyCode != THB, foreignAmount + exchangeRateAtBooking must BOTH be
    // populated > 0 (validated in ArInvoicesService.createInvoice).
    currencyCode: z.string().optional(),
    foreignAmount: z.coerce.number().positive().optional(),
    exchangeRateAtBooking: z.coerce.number().positive().optional(),
    // Maker intent to override the customer credit-limit hold. The SERVER decides
    // whether the user is actually allowed to (ar.invoice.creditoverride); this is
    // only the request to do so. Defaults false.
    creditOverride: z.boolean().optional().default(false),
    lines: z.array(CreateArInvoiceLineSchema).min(1),
});
// ============================================================
// Credit status — used/available credit for a customer (AR-08)
// ============================================================
// DTO returned by GET …/customers/:customerId/credit-status. exposure = current
// outstanding AR (Σ max(0, total−paid−credited) over non-VOID invoices);
// available = creditLimit − exposure (clamped ≥0); overLimit = creditLimit > 0
// && exposure > creditLimit. Decimal fields serialize as canonical strings.
export const CreditStatusSchema = z.object({
    creditLimit: DecimalLike,
    exposure: DecimalLike,
    available: DecimalLike,
    overLimit: z.boolean(),
});
// ============================================================
// ArReceipt + allocations
// ============================================================
export const ArReceiptAllocationSchema = z.object({
    id: z.uuid(),
    receiptId: z.uuid(),
    invoiceId: z.uuid(),
    amount: DecimalLike,
});
export const CreateArReceiptAllocationSchema = z
    .object({
    invoiceId: z.uuid(),
    amount: z.coerce.number().positive(),
    // WHT withheld by the customer on THIS invoice's income (pre-VAT base).
    whtAmount: z.coerce.number().nonnegative().default(0),
    whtRate: z.coerce.number().min(0).max(100).optional(),
    incomeType: z.string().optional(),
})
    .refine((a) => a.whtAmount < a.amount, {
    message: 'WHT ต้องน้อยกว่ายอดที่รับชำระ (เงินสุทธิต้อง > 0)',
    path: ['whtAmount'],
});
export const ArReceiptSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    voucherNo: z.string().min(1),
    receiptDate: z.coerce.date(),
    customerId: z.uuid(),
    cashAccountCode: z.string().min(1),
    totalAmount: DecimalLike,
    description: z.string().nullable().optional(),
    journalEntryId: z.uuid().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const ArReceiptWithAllocationsSchema = ArReceiptSchema.extend({
    allocations: z.array(ArReceiptAllocationSchema),
    customerName: z.string(),
});
export const CreateArReceiptSchema = z.object({
    receiptDate: z.iso.date(),
    customerId: z.uuid(),
    cashAccountCode: z.string().min(1),
    description: z.string().optional(),
    // Receipt-level 50-ทวิ reference (one cert per payment). When present the
    // created ArWhtCert rows are RECEIVED; otherwise PENDING (chase later).
    whtCertNo: z.string().optional(),
    whtCertDate: z.iso.date().optional(),
    allocations: z.array(CreateArReceiptAllocationSchema).min(1),
});
// ============================================================
// ArWhtCert — withholding-tax certificate (50-ทวิ) read shape
// ============================================================
export const ArWhtCertSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    receiptId: z.uuid(),
    invoiceId: z.uuid(),
    customerId: z.uuid(),
    incomeType: z.string(),
    incomeDesc: z.string().nullable().optional(),
    base: DecimalLike,
    rate: DecimalLike,
    whtAmount: DecimalLike,
    pndType: z.string(),
    payerTaxId: z.string().nullable().optional(),
    certNo: z.string().nullable().optional(),
    certDate: z.coerce.date().nullable().optional(),
    status: ArWhtCertStatusSchema,
    createdAt: z.coerce.date(),
});
// ============================================================
// Aging report
// ============================================================
export const ArAgingBucketSchema = z.object({
    notDue: DecimalLike,
    d1_30: DecimalLike,
    d31_60: DecimalLike,
    d61_90: DecimalLike,
    over90: DecimalLike,
});
export const ArAgingCustomerRowSchema = z.object({
    customerId: z.uuid(),
    customerCode: z.string(),
    customerName: z.string(),
    notDue: DecimalLike,
    d1_30: DecimalLike,
    d31_60: DecimalLike,
    d61_90: DecimalLike,
    over90: DecimalLike,
    total: DecimalLike,
});
export const ArAgingResponseSchema = z.object({
    asOfDate: z.coerce.date(),
    totals: ArAgingBucketSchema,
    grandTotal: DecimalLike,
    customers: z.array(ArAgingCustomerRowSchema),
});
// ============================================================
// AR Credit Note (ใบลดหนี้) — Phase 2b
// ============================================================
// Standard credit-note reason codes (editable label in the UI). The free-text
// reasonNote captures the specifics; reasonCode drives reporting/RD typing.
export const AR_CREDIT_NOTE_REASONS = {
    price_reduction: 'ลดราคา',
    discount: 'ส่วนลด',
    damaged: 'สินค้าชำรุด',
    short_ship: 'ส่งไม่ครบ',
    return: 'รับคืนสินค้า',
    cancel: 'ยกเลิกรายการ',
};
// Engine/lifecycle state for a credit note. DRAFT → PENDING (submitted to the
// approval engine) → POSTED (approved + JV booked) or VOID; RETURNED is the
// engine's send-back-to-maker state.
export const ArCreditNoteStatusSchema = z.enum(['DRAFT', 'PENDING', 'POSTED', 'VOID', 'RETURNED']);
// ------------------------------------------------------------
// ArCreditNoteLine — child rows of an ArCreditNote
// ------------------------------------------------------------
export const ArCreditNoteLineSchema = z.object({
    id: z.uuid(),
    creditNoteId: z.uuid(),
    lineNo: z.number().int().positive(),
    description: z.string().min(1),
    qty: DecimalLike,
    unitPrice: DecimalLike,
    amount: DecimalLike,
    revenueCode: z.string().min(1),
});
// Wire shape — id/creditNoteId/lineNo/amount are server-derived. Clones
// CreateArInvoiceLineSchema (the line economics mirror an invoice line).
export const CreateArCreditNoteLineSchema = z.object({
    description: z.string().min(1),
    qty: z.coerce.number().positive(),
    unitPrice: z.coerce.number().nonnegative(),
    revenueCode: z.string().min(1),
});
// ------------------------------------------------------------
// ArCreditNote — header
// ------------------------------------------------------------
// Approval-chain row mirroring ar_credit_note_approvals (maker → reviewer(s) →
// approver). Surfaced on the detail so the web ApprovalTimeline can render it.
export const ArCreditNoteApprovalRowSchema = z.object({
    id: z.uuid(),
    stepNo: z.number().int().positive(),
    roleCode: z.string(),
    roleLabel: z.string(),
    approvedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    comment: z.string().nullable().optional(),
});
export const ArCreditNoteSchema = z.object({
    id: z.uuid(),
    companyId: z.uuid(),
    voucherNo: z.string().min(1),
    creditNoteDate: z.coerce.date(),
    invoiceId: z.uuid(),
    customerId: z.uuid(),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    reasonCode: z.string().min(1),
    reasonNote: z.string().nullable().optional(),
    subtotal: DecimalLike,
    vatRate: DecimalLike,
    vatAmount: DecimalLike,
    totalAmount: DecimalLike,
    status: ArCreditNoteStatusSchema,
    journalEntryId: z.uuid().nullable().optional(),
    postedBy: z.string().nullable().optional(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    // Engine-approval columns (kept SEPARATE from the accounting `status`).
    // Null/0 for a credit note that has not entered the engine.
    approvalStatus: z.string().nullable().optional(),
    currentStep: z.coerce.number().int().default(0),
    submittedBy: z.string().nullable().optional(),
    submittedAt: z.coerce.date().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
    approvedAt: z.coerce.date().nullable().optional(),
    rejectedReason: z.string().nullable().optional(),
    returnedReason: z.string().nullable().optional(),
});
// Detail shape — includes lines + the approval chain + display names.
export const ArCreditNoteWithLinesSchema = ArCreditNoteSchema.extend({
    lines: z.array(ArCreditNoteLineSchema),
    approvals: z.array(ArCreditNoteApprovalRowSchema).default([]),
    customerName: z.string(),
    invoiceVoucherNo: z.string().nullable().optional(),
    branchName: z.string().nullable().optional(),
});
// Wire shape for POST — voucherNo / subtotal / vatAmount / totalAmount are all
// generated server-side. Dates are wire-as-ISO per Phase 3b convention.
export const CreateArCreditNoteSchema = z.object({
    creditNoteDate: z.iso.date(),
    invoiceId: z.uuid(),
    reasonCode: z.string().min(1),
    reasonNote: z.string().optional(),
    branchId: z.uuid().nullable().optional(),
    departmentId: z.uuid().nullable().optional(),
    vatRate: z.coerce.number().min(0).max(100).default(7),
    lines: z.array(CreateArCreditNoteLineSchema).min(1),
});
// ============================================================
// AR Invoice Void (ยกเลิกใบแจ้งหนี้) — Phase 2c (AR-07)
// ============================================================
// Standard void reason codes (editable label in the UI). The free-text
// reasonNote captures the specifics; reasonCode drives reporting. Voiding is
// reserved for a posted, untouched invoice (a paid/credited invoice must use a
// credit note instead).
export const AR_VOID_REASONS = {
    duplicate: 'ออกซ้ำ',
    wrong_customer: 'ผิดลูกค้า',
    wrong_amount: 'จำนวนผิด',
    data_entry_error: 'คีย์ผิด',
    cancelled_order: 'ยกเลิกคำสั่งซื้อ',
};
// Engine/lifecycle state for a void request. DRAFT → PENDING (submitted to the
// approval engine) → POSTED (approved + reversing JV booked) or VOID (rejected);
// RETURNED is the engine's send-back-to-maker state. Mirrors ArCreditNoteStatus.
export const ArInvoiceVoidStatusSchema = z.enum(['DRAFT', 'PENDING', 'POSTED', 'VOID', 'RETURNED']);
// Wire shape for POST — voucherNo is generated server-side; branch/department
// are copied from the target invoice server-side (not user-supplied). Dates are
// wire-as-YYYY-MM-DD.
export const CreateArInvoiceVoidSchema = z.object({
    invoiceId: z.uuid(),
    voidDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'voidDate must be YYYY-MM-DD'),
    reasonCode: z.string().min(1).max(40),
    reasonNote: z.string().max(500).optional(),
});
// Response/read shape for a void request. amount = the voided invoice's
// totalAmount (matrix-tier basis). Engine-approval columns are kept SEPARATE
// from the accounting `status` (Design-Z, mirrors ArCreditNote).
export const ArInvoiceVoidSchema = z.object({
    id: z.string(),
    companyId: z.string(),
    voucherNo: z.string(),
    invoiceId: z.string(),
    invoiceVoucherNo: z.string().optional(),
    customerId: z.string(),
    customerName: z.string().optional(),
    voidDate: z.union([z.string(), z.date()]),
    reasonCode: z.string(),
    reasonNote: z.string().nullable().optional(),
    branchId: z.string().nullable().optional(),
    branchName: z.string().nullable().optional(),
    departmentId: z.string().nullable().optional(),
    amount: DecimalLike,
    status: ArInvoiceVoidStatusSchema,
    approvalStatus: z.string().nullable().optional(),
    currentStep: z.number().optional(),
    journalEntryId: z.string().nullable().optional(),
    submittedBy: z.string().nullable().optional(),
    approvedBy: z.string().nullable().optional(),
});
// ============================================================
// Customer Statement of Account (ใบแจ้งยอดบัญชีลูกหนี้) — Phase 3b (AR-11)
// ============================================================
//
// Read-only report over existing data (no schema change). Two switchable modes:
//   - activity (balance-forward): opening balance as of `from`, every AR-affecting
//     event in [from, to] with a running balance, closing balance as of `to`.
//   - openItem: the customer's currently-open invoices (outstanding > 0), aged
//     as-of a date, with the 5-bucket summary + grand total.
// A statement is an invoice-level read — REUSE the `ar.invoice.read` permission;
// no new permission key.
export const StatementModeSchema = z.enum(['activity', 'openItem']);
// Query params for the statement endpoints. Dates are wire-as-YYYY-MM-DD.
// `from`/`to` drive activity mode; `asOf` drives open-item aging (default today).
export const StatementQuerySchema = z.object({
    mode: StatementModeSchema,
    from: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    to: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    asOf: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
});
// One activity-mode line: a normalized AR-affecting event with a running balance.
// debit/credit are non-negative magnitudes (invoice→debit; receipt/CN/void→credit);
// `balance` is the SIGNED running balance (can go negative after over-receipt).
export const StatementActivityLineSchema = z.object({
    date: z.union([z.string(), z.date()]),
    kind: z.enum(['invoice', 'receipt', 'creditNote', 'void']),
    ref: z.string(),
    description: z.string().optional(),
    debit: DecimalLike,
    credit: DecimalLike,
    balance: SignedDecimalLike,
});
// One open-item line: an outstanding (>0) non-VOID invoice aged vs the as-of date.
export const StatementOpenItemLineSchema = z.object({
    voucherNo: z.string(),
    invoiceDate: z.union([z.string(), z.date()]),
    dueDate: z.union([z.string(), z.date()]),
    total: DecimalLike,
    paid: DecimalLike,
    credited: DecimalLike,
    outstanding: DecimalLike,
    agingDays: z.number(),
    bucket: z.string(),
});
// The full statement DTO — header (customer + company + mode + range) plus the
// mode-specific lines and summary. `opening`/`closing` are SIGNED (over-receipt
// can push a balance negative); the activity-mode lines + opening/closing are
// present in activity mode, openItemLines + buckets + total in open-item mode.
export const CustomerStatementSchema = z.object({
    companyId: z.string(),
    customerId: z.string(),
    customerCode: z.string(),
    customerName: z.string(),
    taxId: z.string().nullable().optional(),
    terms: z.string().nullable().optional(),
    creditLimit: DecimalLike,
    mode: StatementModeSchema,
    from: z.string().nullable().optional(),
    to: z.string().nullable().optional(),
    asOf: z.string().nullable().optional(),
    generatedAt: z.union([z.string(), z.date()]),
    // activity mode
    activityLines: z.array(StatementActivityLineSchema).optional(),
    opening: SignedDecimalLike.optional(),
    closing: SignedDecimalLike.optional(),
    // open-item mode
    openItemLines: z.array(StatementOpenItemLineSchema).optional(),
    buckets: z
        .object({
        notDue: DecimalLike,
        d1_30: DecimalLike,
        d31_60: DecimalLike,
        d61_90: DecimalLike,
        over90: DecimalLike,
    })
        .optional(),
    total: DecimalLike.optional(),
});
// ============================================================
// AR Billing Note (ใบวางบิล) — Phase 3c (AR-09)
// ============================================================
//
// Operational collection cover-document bundling a customer's unpaid invoices
// for a scheduled payment / cheque pickup. Posts NO journal entry (RBAC-only,
// no approval engine). Lifecycle: DRAFT → ISSUED → CANCELLED. A derived
// `collected` badge (NOT a stored status) is true when every linked invoice's
// live outstanding is 0.
export const ArBillingNoteStatusSchema = z.enum(['DRAFT', 'ISSUED', 'CANCELLED']);
// Wire shape for POST — voucherNo / totalAmount / line snapshots are generated
// server-side. Dates are wire-as-YYYY-MM-DD (Phase 3b convention). invoiceIds
// must have ≥ 1; the service validates each invoice (customer/company, POSTED,
// outstanding > 0, not already on an active note).
export const CreateArBillingNoteSchema = z.object({
    customerId: z.string().uuid(),
    invoiceIds: z.array(z.string().uuid()).min(1),
    billingDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    dueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    branchId: z.string().uuid().nullable().optional(),
    departmentId: z.string().uuid().nullable().optional(),
    note: z.string().max(500).optional(),
});
// Body for POST :id/cancel — a non-empty cancellation reason (audited).
export const CancelArBillingNoteSchema = z.object({
    reason: z.string().min(1).max(500),
});
// One billing-note line. `amount` is the invoice's outstanding SNAPSHOT at
// billing time (point-in-time, for the historical doc/PDF); `outstanding` is the
// invoice's LIVE outstanding (read fresh, for the collected badge + receipt
// shortcut) — keep the two distinct.
export const ArBillingNoteLineSchema = z.object({
    id: z.string().optional(),
    invoiceId: z.string(),
    invoiceVoucherNo: z.string().optional(),
    invoiceDate: z.union([z.string(), z.date()]).optional(),
    dueDate: z.union([z.string(), z.date()]).optional(),
    amount: DecimalLike,
    outstanding: DecimalLike.optional(),
});
// Read/response shape — header + enriched display names + derived `collected`
// (every linked invoice's live outstanding == 0) + `outstandingTotal` (Σ live
// outstanding of the lines). `totalAmount` is the Σ of the snapshot amounts.
export const ArBillingNoteSchema = z.object({
    id: z.string(),
    companyId: z.string(),
    voucherNo: z.string(),
    billingDate: z.union([z.string(), z.date()]),
    dueDate: z.union([z.string(), z.date()]),
    customerId: z.string(),
    customerName: z.string().optional(),
    branchId: z.string().nullable().optional(),
    branchName: z.string().nullable().optional(),
    departmentId: z.string().nullable().optional(),
    note: z.string().nullable().optional(),
    totalAmount: DecimalLike,
    outstandingTotal: DecimalLike.optional(),
    status: ArBillingNoteStatusSchema,
    collected: z.boolean().optional(),
    createdBy: z.string().nullable().optional(),
    issuedBy: z.string().nullable().optional(),
    issuedAt: z.union([z.string(), z.date()]).nullable().optional(),
    cancelledBy: z.string().nullable().optional(),
    cancelReason: z.string().nullable().optional(),
    lines: z.array(ArBillingNoteLineSchema).optional(),
});
// ============================================================
// AR Post-Dated Cheque (PDC) Register (เช็คลงวันที่ล่วงหน้า) — Phase 4a (AR-10)
// ============================================================
//
// Register/memo of customer post-dated cheques. A PDC posts NO journal entry of
// its own — the AR stays outstanding until the cheque CLEARS, at which point the
// PDC triggers the existing receipt flow (Dr Cash / Cr AR via ArReceiptsService).
// Lifecycle: RECEIVED → DEPOSITED → CLEARED (terminal) or … → BOUNCED (terminal).
// RBAC-only, no approval engine (operational, like the billing note).
export const ArPdcStatusSchema = z.enum(['RECEIVED', 'DEPOSITED', 'CLEARED', 'BOUNCED']);
// One intended settlement of the cheque against an invoice. `amount` mirrors the
// receipt/billing-note allocation convention (coerce-positive); the clearing
// receipt re-caps each to the invoice's live outstanding (its own TOCTOU guard).
export const ArPdcAllocationInputSchema = z.object({
    invoiceId: z.string().uuid(),
    amount: z.coerce.number().positive(),
});
// Wire shape for POST — voucherNo is generated server-side. Dates are
// wire-as-YYYY-MM-DD (Phase 3b convention). Σ allocations.amount must be ≤ the
// cheque amount (can't allocate more than the cheque); the service additionally
// validates each invoice (customer/company, POSTED, outstanding > 0).
export const CreateArPdcSchema = z
    .object({
    customerId: z.string().uuid(),
    chequeNo: z.string().min(1).max(50),
    bankName: z.string().min(1).max(100),
    bankBranch: z.string().max(100).optional(),
    chequeDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    amount: z.coerce.number().positive(),
    receivedDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    branchId: z.string().uuid().nullable().optional(),
    departmentId: z.string().uuid().nullable().optional(),
    note: z.string().max(500).optional(),
    allocations: z.array(ArPdcAllocationInputSchema).default([]),
})
    .refine((p) => p.allocations.reduce((sum, a) => sum + a.amount, 0) <= p.amount, {
    message: 'cannot allocate more than the cheque amount',
    path: ['allocations'],
});
// Body for POST :id/clear — pick the company cash/bank COA the cheque cleared
// into + the cleared date. Drives the receipt the clear creates (Dr Cash/Cr AR).
export const ClearArPdcSchema = z.object({
    cashAccountCode: z.string().min(1),
    clearedDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
// Body for POST :id/bounce — a non-empty bounce reason (audited). No JV: AR was
// never settled, so a bounce only flips status (AR stays outstanding).
export const BounceArPdcSchema = z.object({
    reason: z.string().min(1).max(500),
});
// Body for POST :id/deposit — optionally record the deposit bank COA + date.
// Both optional (the date defaults to today server-side).
export const DepositArPdcSchema = z.object({
    depositBankCode: z.string().optional(),
    depositedDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
});
// One enriched allocation row on the read shape — the intended settlement plus
// the invoice's voucherNo + live outstanding (for the register/clear UI).
export const ArPdcAllocationSchema = z.object({
    invoiceId: z.string(),
    invoiceVoucherNo: z.string().optional(),
    amount: DecimalLike,
    outstanding: DecimalLike.optional(),
});
// Read/response shape — header + enriched display names + derived `dueInDays`
// (chequeDate − today, for the due-soon cue) + optional allocations. Lifecycle
// dates (depositedDate/clearedDate/bouncedDate) and receiptId populate as the
// cheque progresses. Date fields accept string | Date.
export const ArPdcSchema = z.object({
    id: z.string(),
    companyId: z.string(),
    voucherNo: z.string(),
    customerId: z.string(),
    customerName: z.string().optional(),
    chequeNo: z.string(),
    bankName: z.string(),
    bankBranch: z.string().nullable().optional(),
    chequeDate: z.union([z.string(), z.date()]),
    amount: DecimalLike,
    branchId: z.string().nullable().optional(),
    branchName: z.string().nullable().optional(),
    departmentId: z.string().nullable().optional(),
    note: z.string().nullable().optional(),
    status: ArPdcStatusSchema,
    receivedDate: z.union([z.string(), z.date()]),
    depositedDate: z.union([z.string(), z.date()]).nullable().optional(),
    clearedDate: z.union([z.string(), z.date()]).nullable().optional(),
    bouncedDate: z.union([z.string(), z.date()]).nullable().optional(),
    bounceReason: z.string().nullable().optional(),
    depositBankCode: z.string().nullable().optional(),
    receiptId: z.string().nullable().optional(),
    dueInDays: z.number().optional(),
    createdBy: z.string().nullable().optional(),
    allocations: z.array(ArPdcAllocationSchema).optional(),
});
// ============================================================
// AR Customer Advance / Deposit (เงินรับล่วงหน้า/มัดจำ) — Phase 4b (AR-12)
// ============================================================
//
// Cash a customer pays BEFORE an invoice exists. Received against a LIABILITY
// (CUSTOMER_ADVANCE = 20200002), not revenue/AR. Later APPLIED to one or more
// invoices (Dr CUSTOMER_ADVANCE / Cr AR_TRADE + bump invoice.paidAmount) or
// REFUNDED (Dr CUSTOMER_ADVANCE / Cr cash) for the unused balance. RBAC-only,
// no approval engine (operational cash like a receipt).
// Lifecycle: OPEN while balance = amount − appliedAmount − refundedAmount > 0;
// CLOSED when balance hits 0 (fully applied and/or refunded).
export const ArAdvanceStatusSchema = z.enum(['OPEN', 'CLOSED']);
// Wire shape for POST — voucherNo (ADV-YYMM-NNNNNN) is generated server-side.
// Dates are wire-as-YYYY-MM-DD (Phase 3b convention). The service posts the
// receive JV (Dr cashAccountCode / Cr CUSTOMER_ADVANCE) for `amount`.
export const CreateArAdvanceSchema = z.object({
    customerId: z.string().uuid(),
    advanceDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    amount: z.coerce.number().positive(),
    cashAccountCode: z.string().min(1),
    branchId: z.string().uuid().nullable().optional(),
    departmentId: z.string().uuid().nullable().optional(),
    note: z.string().max(500).optional(),
});
// Body for POST :id/apply — apply part of the advance balance to one of the
// customer's open invoices. `amount` is capped server-side to min(advance
// balance, invoice outstanding) via an in-tx TOCTOU re-check. appliedDate
// defaults to today server-side.
export const ApplyArAdvanceSchema = z.object({
    invoiceId: z.string().uuid(),
    amount: z.coerce.number().positive(),
    appliedDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
});
// Body for POST :id/refund — refund the remaining (unapplied) balance back to a
// company cash/bank COA. refundDate defaults to today server-side.
export const RefundArAdvanceSchema = z.object({
    cashAccountCode: z.string().min(1),
    refundDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
});
// One enriched application row on the read shape — an apply event with the
// invoice's voucherNo + the apply JV id. Date fields accept string | Date.
export const ArAdvanceApplicationSchema = z.object({
    id: z.string().optional(),
    invoiceId: z.string(),
    invoiceVoucherNo: z.string().optional(),
    amount: DecimalLike,
    appliedDate: z.union([z.string(), z.date()]).optional(),
    journalEntryId: z.string().nullable().optional(),
});
// Read/response shape — header + enriched display names + derived `balance`
// (amount − appliedAmount − refundedAmount) + optional applications. journalEntryId
// is the receive JV; refundJournalEntryId populates on refund. Date fields accept
// string | Date.
export const ArAdvanceSchema = z.object({
    id: z.string(),
    companyId: z.string(),
    voucherNo: z.string(),
    customerId: z.string(),
    customerName: z.string().optional(),
    advanceDate: z.union([z.string(), z.date()]),
    amount: DecimalLike,
    appliedAmount: DecimalLike,
    refundedAmount: DecimalLike,
    balance: DecimalLike,
    cashAccountCode: z.string(),
    branchId: z.string().nullable().optional(),
    branchName: z.string().nullable().optional(),
    departmentId: z.string().nullable().optional(),
    note: z.string().nullable().optional(),
    status: ArAdvanceStatusSchema,
    journalEntryId: z.string().nullable().optional(),
    refundJournalEntryId: z.string().nullable().optional(),
    createdBy: z.string().nullable().optional(),
    applications: z.array(ArAdvanceApplicationSchema).optional(),
});
// ============================================================
// AR DSO (Days Sales Outstanding) KPI Dashboard — Phase 4c (AR-13)
// ============================================================
//
// Server-computed read-only KPI dashboard at /ar/dso. GROSS basis (AR + sales
// both use invoice totalAmount, VAT-inclusive); VOID invoices excluded. Reuses
// the `ar.aging.read` permission (DSO is an aging/analytics read — NO new key).
// `dso` is a rounded integer day count, NULL when trailing-365 sales ≤ 0
// (divide-by-zero sentinel). Money fields serialize as canonical strings.
// One point on the 12-month DSO trend. `period` is 'YYYY-MM' (a month-end);
// `dso` is the point-in-time DSO (null when that month's trailing sales = 0).
export const DsoTrendPointSchema = z.object({
    period: z.string(),
    dso: z.number().nullable(),
    ar: DecimalLike,
    sales: DecimalLike,
});
// One by-customer row (slowest payers). `dso` null when the customer's
// trailing-365 sales = 0 (e.g. AR from older invoices, no recent sales).
export const DsoCustomerRowSchema = z.object({
    customerId: z.string(),
    customerName: z.string(),
    ar: DecimalLike,
    sales: DecimalLike,
    dso: z.number().nullable(),
});
// The full DSO dashboard DTO. `overall.dso`/`bpdso`/`add` are null when sales
// are 0. `add` (Average Days Delinquent) = overall DSO − bpdso (≥ 0). Aging
// split: notDueAR (dueDate > asOf) vs overdueAR (dueDate ≤ asOf) of the current
// outstanding; overduePct = overdueAR / totalAR × 100.
export const DsoDashboardSchema = z.object({
    asOf: z.union([z.string(), z.date()]),
    days: z.number(),
    overall: z.object({
        dso: z.number().nullable(),
        ar: DecimalLike,
        sales: DecimalLike,
    }),
    bpdso: z.number().nullable(),
    add: z.number().nullable(),
    totalAR: DecimalLike,
    notDueAR: DecimalLike,
    overdueAR: DecimalLike,
    overduePct: z.number(),
    trend: z.array(DsoTrendPointSchema),
    byCustomer: z.array(DsoCustomerRowSchema),
});
//# sourceMappingURL=ar.js.map